/**
 * Where a connector actually goes.
 *
 * The endpoint is solved against the shape's CURRENT rect on every
 * render rather than stored — that is the whole reason a line follows a
 * box when you drag it, and the difference between this and a fixed-port
 * model where an arrow tears off its shape the moment anything moves.
 *
 * A connector attaches to the SHAPE, not to a port. Which side it leaves
 * from is decided by where the other end is, so a deed box dragged from
 * under a party to beside it re-routes out of the correct face without
 * anybody re-attaching anything.
 */
import { center, centerX, centerY, bottom, right } from './geometry.js';

const SIDES = ['top', 'right', 'bottom', 'left'];

export function sidePoint(rect, side, along = 0.5) {
  // How far along that side, 0 at the left or top corner and 1 at the
  // other. The middle by default, which is where every connector used to
  // be pinned; a fraction rather than a distance so a point keeps its
  // place along the side when the box is made wider, and so it can never
  // end up off the end of the side it belongs to.
  const t = Math.max(0, Math.min(1, Number.isFinite(Number(along)) ? Number(along) : 0.5));
  switch (side) {
    case 'top': return { x: rect.x + rect.w * t, y: rect.y };
    case 'bottom': return { x: rect.x + rect.w * t, y: bottom(rect) };
    case 'left': return { x: rect.x, y: rect.y + rect.h * t };
    case 'right': return { x: right(rect), y: rect.y + rect.h * t };
    default: return center(rect);
  }
}

/**
 * The side a point is NEAREST — which is not the side it is most in the
 * direction of.
 *
 * `faceToward` answers the direction question, and is right for it: a
 * line with no side stored leaves by the face pointing at the other end.
 * It is wrong for a drop. On a box four hundred wide, a point five
 * pixels under the top edge and a hundred left of the middle is further
 * from the centre in x than in y, so faceToward calls it the left side —
 * and the arrow attaches to an edge the pointer was nowhere near.
 *
 * What somebody means by dropping near an edge is that edge.
 */
export function nearestSide(rect, at) {
  const gaps = [
    ['top', Math.abs(at.y - rect.y)],
    ['bottom', Math.abs(rect.y + rect.h - at.y)],
    ['left', Math.abs(at.x - rect.x)],
    ['right', Math.abs(rect.x + rect.w - at.x)],
  ];
  return gaps.reduce((best, one) => (one[1] < best[1] ? one : best))[0];
}

/**
 * Which fraction along a side a point is — the inverse of the above.
 *
 * Used while an endpoint is being dragged: the pointer gives a place on
 * the chart, and this says what to store.
 */
export function alongSide(rect, side, at) {
  if (!rect) return 0.5;
  const t = (side === 'top' || side === 'bottom')
    ? (at.x - rect.x) / Math.max(rect.w, 1e-6)
    : (at.y - rect.y) / Math.max(rect.h, 1e-6);
  return Math.max(0, Math.min(1, t));
}

/** The face pointing most directly at a target. */
export function faceToward(rect, target) {
  const dx = target.x - centerX(rect);
  const dy = target.y - centerY(rect);
  if (Math.abs(dx) > Math.abs(dy)) return dx > 0 ? 'right' : 'left';
  return dy > 0 ? 'bottom' : 'top';
}

/**
 * The named side, when a person pinned one; otherwise the best face.
 *
 * A DEFAULTED SIDE IS DROPPED WHEN THE OTHER END MOVES BEHIND IT. The
 * side is stored so a line keeps leaving the face it was drawn out of,
 * which is right while the boxes stay roughly where they were. Drag the
 * far box round to the other side and it stops being right: the run
 * leaves by the right face, turns round and comes back past the box it
 * just left. That doubling-back is the kink, and no straightening
 * downstream can remove it — the route is already committed to going the
 * wrong way.
 *
 * A REMEMBERED FACE IS KEPT, whatever else moves. This briefly re-faced a
 * connector whose stored side had stopped pointing at the far box, which
 * removed a kink and cost something worth more: an arrow attached to the
 * bottom of a party slid round to its side when a box elsewhere was
 * dragged, so a chart rearranged itself while somebody was tidying it.
 * A line stays on the face it was put on and bends to reach; where it can
 * be straight without moving faces, `straighten` below does that instead.
 */
function endpointOf(rect, end, toward) {
  if (!rect) return end?.x != null ? { x: end.x, y: end.y, side: null } : null;
  const side = end?.side && SIDES.includes(end.side) ? end.side : faceToward(rect, toward);
  return { ...sidePoint(rect, side, end?.along), side };
}

const OUT = 22;
const push = (point, side, by = OUT) => {
  switch (side) {
    case 'top': return { x: point.x, y: point.y - by };
    case 'bottom': return { x: point.x, y: point.y + by };
    case 'left': return { x: point.x - by, y: point.y };
    case 'right': return { x: point.x + by, y: point.y };
    default: return point;
  }
};

const isVertical = (side) => side === 'top' || side === 'bottom';

/**
 * An elbow route between two points that leave their shapes squarely.
 *
 * The stub out of each face is what stops a line grazing the corner of
 * the box it just left — the thing that makes a hand-drawn chart look
 * hand-drawn and a routed one look routed.
 *
 * BOTH FACES DECIDE THE SHAPE OF IT. This used to split the run at the
 * midpoint of whichever axis the FROM side pointed along and ignore the
 * far side entirely, so a line leaving a right face and arriving at a
 * top face — the commonest route on a chain of title, a deed to the
 * party under and beside it — came out as a Z with a doubled-back leg
 * where one corner was the whole answer. Perpendicular faces take the
 * single bend; parallel faces take the split, along the axis they both
 * point down.
 */
function elbow(a, b, aSide, bSide) {
  const start = push(a, aSide);
  const end = push(b, bSide);
  const aVertical = isVertical(aSide);
  const bVertical = isVertical(bSide);
  const points = [a, start];

  if (aVertical !== bVertical) {
    // One corner, and only one: go as far as the arriving stub along the
    // axis this end travels, then turn.
    points.push(aVertical ? { x: start.x, y: end.y } : { x: end.x, y: start.y });
  } else if (aVertical) {
    const midY = (start.y + end.y) / 2;
    points.push({ x: start.x, y: midY }, { x: end.x, y: midY });
  } else {
    const midX = (start.x + end.x) / 2;
    points.push({ x: midX, y: start.y }, { x: midX, y: end.y });
  }
  points.push(end, b);
  return dedupe(points);
}

/**
 * The same run with nothing redundant in it.
 *
 * Duplicate points go, and so do points that sit in the middle of a
 * straight leg — the stub out of a face lands on one whenever the route
 * carries straight on past it. They are invisible as geometry and not as
 * drawing: pathFrom rounds every interior point, so a collinear one
 * became a curve command in the middle of a straight line, and the line
 * picked up a soft spot where Lucid draws it dead straight.
 */
function dedupe(points) {
  const out = [];
  for (const p of points) {
    const last = out[out.length - 1];
    if (last && Math.abs(last.x - p.x) < 0.01 && Math.abs(last.y - p.y) < 0.01) continue;
    out.push(p);
  }
  const trimmed = [];
  for (let i = 0; i < out.length; i += 1) {
    const prev = trimmed[trimmed.length - 1];
    const next = out[i + 1];
    if (prev && next) {
      const cross = (out[i].x - prev.x) * (next.y - out[i].y)
        - (out[i].y - prev.y) * (next.x - out[i].x);
      const forward = (out[i].x - prev.x) * (next.x - out[i].x)
        + (out[i].y - prev.y) * (next.y - out[i].y);
      // Straight on, not doubling back: a reversal is a real corner even
      // though it lies on the same line.
      if (Math.abs(cross) < 0.01 && forward > 0) continue;
    }
    trimmed.push(out[i]);
  }
  return trimmed;
}

/**
 * The full point list for a connector, including any waypoints a person
 * dragged it through. Waypoints win: a route somebody arranged by hand
 * must not be re-derived out from under them.
 */
const OPPOSITE = { top: 'bottom', bottom: 'top', left: 'right', right: 'left' };

/**
 * A line that CAN be straight, is.
 *
 * Two boxes stacked one under the other are almost never aligned to the
 * pixel, and the elbow answered a nine-pixel offset with a nine-pixel
 * jog — a kink halfway down that reads as a mistake in the chart rather
 * than as the truth about where the boxes are. Lucid slides the ends
 * along their faces instead and draws one straight line, which is what
 * somebody stacking a chain of title is trying to draw.
 *
 * Only where a straight line can actually touch both faces: the sides
 * must face each other, and the boxes must overlap across the run, or
 * there is no shared line to sit on. The ends are put at the middle of
 * that overlap, so the line is centred on what the two boxes share
 * rather than on either one of them.
 *
 * NOT WHERE AN END SITS OFF-CENTRE ON ITS FACE. An arrow deliberately
 * put at the left corner of a box must stay there, kink and all. But
 * `along` alone cannot say that: dropping an endpoint anywhere writes
 * it, and the middle is where a drop usually lands — so treating any
 * stored fraction as deliberate refused to straighten almost every line
 * on the chart. Only a fraction that is actually away from the middle
 * counts as somebody's decision.
 */
/**
 * Far enough along a face to have been meant.
 *
 * A twelfth of the face either way. The first threshold here was a
 * hundredth, which on a 170-wide box is under two pixels — so a
 * connector dropped at the middle of a box whose width is not a whole
 * number of grid steps stored 0.503, counted as deliberate, and kept a
 * five-pixel jog forever. Three deeds fanned along one face are still
 * well outside this; a hand aiming at the centre is not.
 */
const OFF_CENTRE = (along) => along != null && Math.abs(along - 0.5) > 0.12;

function straighten(edge, aRect, bRect, a, b) {
  if (!aRect || !bRect) return null;
  if (OFF_CENTRE(edge.from?.along) || OFF_CENTRE(edge.to?.along)) return null;
  // Read off the faces actually chosen, not off what the edge stored: a
  // side that pointed the wrong way has already been dropped by now, and
  // this is exactly the case where straightening is wanted.
  if (OPPOSITE[a.side] !== b.side) return null;

  const vertical = a.side === 'top' || a.side === 'bottom';
  const [lo, hi] = vertical
    ? [Math.max(aRect.x, bRect.x), Math.min(aRect.x + aRect.w, bRect.x + bRect.w)]
    : [Math.max(aRect.y, bRect.y), Math.min(aRect.y + aRect.h, bRect.y + bRect.h)];
  if (hi <= lo) return null;

  const mid = (lo + hi) / 2;
  return vertical
    ? [{ ...a, x: mid }, { ...b, x: mid }]
    : [{ ...a, y: mid }, { ...b, y: mid }];
}

export function routeEdge(edge, rectFor) {
  const fromRect = edge.from?.shapeId ? rectFor(edge.from.shapeId) : null;
  const toRect = edge.to?.shapeId ? rectFor(edge.to.shapeId) : null;

  const fromAnchor = fromRect ? center(fromRect) : { x: edge.from?.x ?? 0, y: edge.from?.y ?? 0 };
  const toAnchor = toRect ? center(toRect) : { x: edge.to?.x ?? 0, y: edge.to?.y ?? 0 };

  const waypoints = edge.waypoints || [];
  const a = endpointOf(fromRect, edge.from, waypoints[0] || toAnchor);
  const b = endpointOf(toRect, edge.to, waypoints[waypoints.length - 1] || fromAnchor);
  if (!a || !b) return [];

  if (waypoints.length) return dedupe([a, ...waypoints, b]);
  if (edge.lineShape === 'straight') return [a, b];

  const flat = straighten(edge, fromRect, toRect, a, b);
  if (flat) return flat;
  return elbow(a, b, a.side, b.side);
}

/** A polyline as an SVG path, with the corners rounded like Lucid's. */
export function pathFrom(points, rounding = 0) {
  if (points.length < 2) return '';
  if (!rounding || points.length === 2) {
    return `M ${points[0].x} ${points[0].y} ` + points.slice(1).map((p) => `L ${p.x} ${p.y}`).join(' ');
  }
  let d = `M ${points[0].x} ${points[0].y}`;
  for (let i = 1; i < points.length - 1; i += 1) {
    const prev = points[i - 1];
    const here = points[i];
    const next = points[i + 1];
    // Never round by more than half the shorter leg, or consecutive
    // corners eat each other and the line visibly detaches.
    const inLen = Math.hypot(here.x - prev.x, here.y - prev.y);
    const outLen = Math.hypot(next.x - here.x, next.y - here.y);
    const r = Math.min(rounding, inLen / 2, outLen / 2);
    if (r < 0.5) { d += ` L ${here.x} ${here.y}`; continue; }
    const t1 = { x: here.x + ((prev.x - here.x) / inLen) * r, y: here.y + ((prev.y - here.y) / inLen) * r };
    const t2 = { x: here.x + ((next.x - here.x) / outLen) * r, y: here.y + ((next.y - here.y) / outLen) * r };
    d += ` L ${t1.x} ${t1.y} Q ${here.x} ${here.y} ${t2.x} ${t2.y}`;
  }
  const last = points[points.length - 1];
  return `${d} L ${last.x} ${last.y}`;
}

/** How long a polyline is, end to end. */
export function lengthOf(points) {
  return legLengths(points || []).total;
}

/** How long a polyline is, and how long each leg is. */
function legLengths(points) {
  const legs = [];
  let total = 0;
  for (let i = 0; i < points.length - 1; i += 1) {
    const len = Math.hypot(points[i + 1].x - points[i].x, points[i + 1].y - points[i].y);
    legs.push(len);
    total += len;
  }
  return { legs, total };
}

/**
 * A point some fraction of the way along the line.
 *
 * By LENGTH, not by leg count. An elbow route is three short legs and
 * one long one, and stepping by leg would make the label leap across the
 * corner and crawl along the straight — which reads as the label
 * refusing to go where it was dragged.
 */
export function pointAtFraction(points, t) {
  if (!points || points.length === 0) return { x: 0, y: 0 };
  if (points.length === 1) return points[0];
  const raw = Number(t);
  const at = Math.max(0, Math.min(1, Number.isFinite(raw) ? raw : 0.5));
  const { legs, total } = legLengths(points);
  if (total === 0) return points[0];

  let walked = 0;
  const want = total * at;
  for (let i = 0; i < legs.length; i += 1) {
    if (walked + legs[i] >= want || i === legs.length - 1) {
      const along = legs[i] === 0 ? 0 : (want - walked) / legs[i];
      return {
        x: points[i].x + (points[i + 1].x - points[i].x) * along,
        y: points[i].y + (points[i + 1].y - points[i].y) * along,
      };
    }
    walked += legs[i];
  }
  return points[points.length - 1];
}

/**
 * Where along the line a dragged point lands, as a fraction.
 *
 * The pointer is projected onto the nearest leg rather than measured
 * from an end: a label dragged out sideways should slide to the closest
 * part of the line, not fly to whichever end happens to be nearer in a
 * straight line.
 */
export function fractionAtPoint(points, at) {
  if (!points || points.length < 2) return 0.5;
  const { legs, total } = legLengths(points);
  if (total === 0) return 0.5;

  let best = { distance: Infinity, along: 0 };
  let walked = 0;
  for (let i = 0; i < legs.length; i += 1) {
    const a = points[i];
    const b = points[i + 1];
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const lengthSq = dx * dx + dy * dy;
    const t = lengthSq === 0 ? 0
      : Math.max(0, Math.min(1, ((at.x - a.x) * dx + (at.y - a.y) * dy) / lengthSq));
    const px = a.x + t * dx;
    const py = a.y + t * dy;
    const distance = Math.hypot(at.x - px, at.y - py);
    if (distance < best.distance) best = { distance, along: walked + t * legs[i] };
    walked += legs[i];
  }
  return Math.max(0, Math.min(1, best.along / total));
}

